import { Link } from 'wouter';
import { formatAUD, formatDateAU } from '../../../../shared/format';
import { countOpenItems } from '../../../../shared/bookGrid';
import { CONSENT_LABEL } from '../../../../shared/cadence';
import type { DashboardRow } from '../../../../shared/types';

/**
 * The book as a card list for phones: the same rows (search, filters and sort
 * already applied) with the fields an adviser checks on the move.
 */
export function BookCards({ rows, total, today, onLogCall }: {
  rows: DashboardRow[];
  total: number;
  today: string;
  onLogCall?: (row: DashboardRow, anchor: HTMLElement) => void;
}) {
  if (rows.length === 0) return <p className="qa-card p-4 text-sm text-ink-mute">No clients match these filters.</p>;
  return (
    <div data-testid="book-cards">
      <ul className="space-y-2">
        {rows.slice(0, 300).map((r) => {
          const callLate = !!r.callDue && r.callDue.slice(0, 10) <= today;
          const reviewLate = !!r.reviewDue && r.reviewDue.slice(0, 10) < today;
          const open = countOpenItems(r.compliance);
          const consentWarn = r.consentStatus === 'lapsed' || r.consentStatus === 'renewalOpen';
          return (
            <li key={r.clientId} className="qa-card p-3">
              <div className="flex items-baseline justify-between gap-2">
                <Link to={`/clients/${r.clientId}`} className="text-sm font-medium text-ink hover:text-rust truncate">{r.sortName ?? r.name}</Link>
                {r.segment && <span className="qa-mono text-[10px] text-ink-mute uppercase shrink-0">{r.segment}</span>}
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                <dt className="text-ink-mute">Net worth</dt><dd className="qa-mono text-right">{formatAUD(r.tnw)}</dd>
                <dt className="text-ink-mute">Call due</dt>
                <dd className={`qa-mono text-right ${callLate ? 'text-rust font-medium' : ''}`}>{r.callDue ? formatDateAU(r.callDue) : '-'}</dd>
                <dt className="text-ink-mute">Review</dt>
                <dd className={`qa-mono text-right ${reviewLate ? 'text-rust font-medium' : ''}`}>{r.reviewDue ? formatDateAU(r.reviewDue) : '-'}</dd>
                <dt className="text-ink-mute">Open items</dt>
                <dd className={`qa-mono text-right ${open ? 'text-rust font-medium' : ''}`}>{open}</dd>
                {r.consentStatus && r.consentStatus !== 'none' && (
                  <>
                    <dt className="text-ink-mute">Fee consent</dt>
                    <dd className={`text-right ${consentWarn ? 'text-rust font-medium' : ''}`}>{CONSENT_LABEL[r.consentStatus]}</dd>
                  </>
                )}
              </dl>
              {onLogCall && (
                <div className="mt-2 flex justify-end">
                  <button type="button" className="qa-book-pop__btn" aria-label={`Log a call with ${r.sortName ?? r.name}`}
                    onClick={(e) => onLogCall(r, e.currentTarget)}>Log call</button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-ink-mute mt-3">{rows.length} of {total} clients{rows.length > 300 ? ', first 300 shown; narrow with search or filters' : ''}.</p>
    </div>
  );
}
